import { supabase } from '@/lib/supabaseClient';
import type { KnowledgeSourceType } from './knowledgeSourceManager';

/**
 * AIC-1.2 — Text-entry CRUD for curated knowledge (platform how-to + report
 * recipes). Writes title+body rows directly to `knowledge_documents` with NO
 * embeddings (the FTS layer from AIC-1.1 indexes them via the generated
 * search_vector). Global vs tenant scope is driven by the `is_global` flag.
 *
 * Deliberately separate from `knowledgeService` (which is file-upload + the now
 * defunct OpenAI embedding path).
 */

export interface KnowledgeEntry {
  id: string;
  title: string;
  body: string;
  source_type: KnowledgeSourceType;
  is_global: boolean;
  tenant_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Raw shape as selected from `knowledge_documents`. */
export interface KnowledgeRowRaw {
  id: string;
  title: string;
  content: string;
  source_type: KnowledgeSourceType;
  is_global: boolean;
  tenant_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NewEntryInput {
  title: string;
  body: string;
  sourceType: KnowledgeSourceType;
  isGlobal: boolean;
  tenantId: string;
  userId: string;
}

const ENTRY_COLUMNS =
  'id, title, content, source_type, is_global, tenant_id, is_active, created_at, updated_at';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True if the value is a well-formed UUID (guards raw interpolation into `.or()`). */
export function isUuid(value: string | null | undefined): boolean {
  return typeof value === 'string' && UUID_RE.test(value);
}

/** Build the insert payload for a text knowledge entry (pure). */
export function buildEntryPayload(input: NewEntryInput) {
  const title = input.title.trim();
  const body = input.body ?? '';
  const slug =
    title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'entry';
  return {
    title,
    content: body,
    file_name: `${slug}.md`,
    file_type: 'text/markdown',
    file_size: body.length,
    source_type: input.sourceType,
    is_global: input.isGlobal,
    tenant_id: input.tenantId,
    uploaded_by: input.userId,
    is_active: true,
    metadata: { kind: 'text_entry' },
  };
}

/** Map a raw DB row to a KnowledgeEntry (content -> body) (pure). */
export function mapRow(row: KnowledgeRowRaw): KnowledgeEntry {
  return {
    id: row.id,
    title: row.title,
    body: row.content,
    source_type: row.source_type,
    is_global: row.is_global,
    tenant_id: row.tenant_id,
    is_active: row.is_active,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

class KnowledgeContentService {
  async createEntry(input: NewEntryInput): Promise<KnowledgeEntry> {
    const { data, error } = await supabase
      .from('knowledge_documents')
      .insert(buildEntryPayload(input))
      .select(ENTRY_COLUMNS)
      .single();
    if (error) throw error;
    return mapRow(data as KnowledgeRowRaw);
  }

  /**
   * List entries of a source type. Returns global entries and/or a tenant's own
   * entries depending on the options.
   */
  async listEntries(
    sourceType: KnowledgeSourceType,
    opts: { tenantId?: string; includeGlobal?: boolean } = {},
  ): Promise<KnowledgeEntry[]> {
    const { tenantId, includeGlobal = true } = opts;
    let query = supabase
      .from('knowledge_documents')
      .select(ENTRY_COLUMNS)
      .eq('source_type', sourceType)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    // Only interpolate tenantId into the `.or()` mini-language if it's a real
    // UUID — never trust it raw in a filter grammar.
    const safeTenantId = isUuid(tenantId) ? tenantId : undefined;
    if (safeTenantId && includeGlobal) {
      query = query.or(`is_global.eq.true,tenant_id.eq.${safeTenantId}`);
    } else if (safeTenantId) {
      query = query.eq('tenant_id', safeTenantId);
    } else if (includeGlobal) {
      query = query.eq('is_global', true);
    }

    const { data, error } = await query;
    if (error) {
      console.error('[knowledgeContentService] listEntries failed:', error);
      return [];
    }
    return (data ?? []).map((r) => mapRow(r as KnowledgeRowRaw));
  }

  async updateEntry(
    id: string,
    updates: Partial<Pick<KnowledgeEntry, 'title' | 'body' | 'is_global'>>,
  ): Promise<void> {
    const patch: Record<string, unknown> = {};
    if (updates.title !== undefined) patch.title = updates.title.trim();
    if (updates.body !== undefined) {
      if (updates.body.trim().length === 0) {
        throw new Error('Knowledge entry body cannot be empty.');
      }
      patch.content = updates.body;
      patch.file_size = updates.body.length;
    }
    if (updates.is_global !== undefined) patch.is_global = updates.is_global;
    const { error } = await supabase.from('knowledge_documents').update(patch).eq('id', id);
    if (error) throw error;
  }

  async deleteEntry(id: string): Promise<boolean> {
    // .select() returns the affected rows so we can detect a 0-row no-op (e.g.
    // an RLS policy silently filtering the row) instead of reporting a phantom
    // success.
    const { data, error } = await supabase
      .from('knowledge_documents')
      .delete()
      .eq('id', id)
      .select('id');
    if (!error && data && data.length > 0) return true;

    // Fall back to soft delete if hard delete is blocked by policy.
    const { data: softData, error: softErr } = await supabase
      .from('knowledge_documents')
      .update({ is_active: false })
      .eq('id', id)
      .select('id');
    if (softErr || !softData || softData.length === 0) {
      console.error('[knowledgeContentService] deleteEntry affected 0 rows:', softErr);
      return false;
    }
    return true;
  }
}

export const knowledgeContentService = new KnowledgeContentService();
